<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Households</h1>

    <!-- Households List -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Households</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <input
          v-model="filters.community"
          type="text"
          placeholder="Community"
          aria-label="Filter by community"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
        />

        <select
          v-model="filters.status"
          aria-label="Filter by status"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          v-model="filters.hasContainer"
          aria-label="Filter by container assignment"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">All</option>
          <option value="true">Has Container</option>
          <option value="false">No Container</option>
        </select>

        <button
          @click="reload"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Apply Filters
        </button>
      </div>

      <AlertBanner
        v-if="error && households.length"
        type="error"
        message="Couldn't refresh households — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <AlertBanner
        v-else-if="!loading && limitReached"
        type="info"
        :message="`Showing the first ${households.length} results — narrow your filters to see the rest.`"
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading households...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !households.length"
        title="Couldn't load households"
        :message="error"
        @retry="reload"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No households found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th :aria-sort="ariaSort('villaNumber')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('villaNumber')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Villa - Community
                  <span v-if="sortState.key === 'villaNumber'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('currentContainerSerial')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('currentContainerSerial')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Current Container
                  <span v-if="sortState.key === 'currentContainerSerial'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('totalVolumeCollected')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('totalVolumeCollected')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Total Volume (L)
                  <span v-if="sortState.key === 'totalVolumeCollected'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('lastCollectionDate')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('lastCollectionDate')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Last Collection
                  <span v-if="sortState.key === 'lastCollectionDate'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('status')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('status')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Status
                  <span v-if="sortState.key === 'status'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="household in sortedAndFiltered" :key="household.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                <span class="font-medium">{{ household.villaNumber || 'N/A' }}</span>
                <span v-if="household.community" class="text-neutral-500 dark:text-neutral-400"> - {{ household.community }}</span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span
                  v-if="household.currentContainerSerial || household.currentContainerId"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  {{ household.currentContainerSerial || household.currentContainerId }}
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200"
                >
                  No Container
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span class="font-bold tabular-nums text-green-600 dark:text-green-400">
                  {{ household.totalVolumeCollected || household.totalVolumeCollectedL || 0 }}L
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                {{ household.lastCollectionDate ? (household.lastCollectionDate).slice(0,10) : 'No collections' }}
              </td>
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': household.status === 'active',
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': household.status === 'inactive',
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !household.status || (household.status !== 'active' && household.status !== 'inactive')
                  }"
                >
                  {{ household.status || 'active' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <button
                  @click="viewHistory(household)"
                  class="text-sm px-3 py-1.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  View History
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-6">
        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Showing: {{ households.length }}
        </span>
      </div>
    </div>

    <!-- History Modal -->
    <Modal :show="showHistoryModal" size="xl" :close-on-backdrop="true" @close="closeHistoryModal">
      <template #header>
        <h3 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
          Household History -
          <span v-if="selectedHousehold?.villaNumber">{{ selectedHousehold.villaNumber }}</span>
          <span v-if="selectedHousehold?.community" class="text-neutral-600 dark:text-neutral-400"> - {{ selectedHousehold.community }}</span>
          <span v-if="!selectedHousehold?.villaNumber" class="text-neutral-600 dark:text-neutral-400">{{ selectedHousehold?.id }}</span>
        </h3>
      </template>

      <div v-if="historyLoading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading history...</p>
        </div>
      </div>

      <ErrorState
        v-else-if="historyError"
        title="Couldn't load household history"
        :message="historyError"
        @retry="loadHistory"
      />

      <div v-else-if="householdHistory" class="space-y-6">
        <!-- Household Info -->
        <div class="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-4">
          <h4 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Household Details</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Location</p>
              <p class="text-base font-medium text-neutral-900 dark:text-neutral-100">
                {{ householdHistory.household?.villaNumber || 'N/A' }}
                <span v-if="householdHistory.household?.community" class="text-neutral-600 dark:text-neutral-400"> - {{ householdHistory.household.community }}</span>
              </p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Current Container</p>
              <p class="text-base font-medium text-neutral-900 dark:text-neutral-100">
                {{ householdHistory.household?.currentContainerSerial || householdHistory.household?.currentContainerId || 'None' }}
              </p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Total Volume Collected</p>
              <p class="text-base font-medium tabular-nums text-neutral-900 dark:text-neutral-100">{{ householdHistory.totalVolumeCollectedL || 0 }}L</p>
            </div>
            <div>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">Household ID</p>
              <p class="text-base font-mono text-sm text-neutral-700 dark:text-neutral-300">{{ householdHistory.household?.id }}</p>
            </div>
          </div>
        </div>

        <!-- Assignments -->
        <div v-if="householdHistory.assignments?.length" class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
          <h4 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Container Assignments</h4>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Container Serial</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Assigned At</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Assigned By</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
                <tr v-for="assignment in householdHistory.assignments" :key="assignment.containerId" class="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ assignment.containerSerial || assignment.containerId }}</td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(assignment.assignedAt) }}</td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ assignment.assignedBy || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Collections History -->
        <div v-if="householdHistory.collections?.length" class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
          <h4 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Collection History</h4>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Date</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Container Serial</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Volume (L)</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Weight (kg)</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Collected By</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
                <tr v-for="collection in householdHistory.collections" :key="collection.id || collection.requestId" class="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                    {{ formatDateTime(collection.completedAt || collection.performedAt || collection.requestedAt) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                    {{ collection.containerSerial || collection.containerId || '-' }}
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span class="font-bold tabular-nums text-green-600 dark:text-green-400">
                      {{ collection.volumeL || collection.metrics?.volumeL || '-' }}{{ collection.volumeL || collection.metrics?.volumeL ? 'L' : '' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span class="font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
                      {{ collection.weightKg || collection.metrics?.weightKg || '-' }}{{ collection.weightKg || collection.metrics?.weightKg ? 'kg' : '' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                    {{ collection.performedBy || collection.assignedTo || '-' }}
                  </td>
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      :class="{
                        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': collection.status === 'completed',
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': collection.status === 'delivered',
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': collection.status === 'requested',
                        'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !['completed', 'delivered', 'requested'].includes(collection.status)
                      }"
                    >
                      {{ collection.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Deployments -->
        <div v-if="householdHistory.deployments?.length" class="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
          <h4 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Deployments & Swaps</h4>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Type</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Container(s)</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Performed At</th>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Performed By</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
                <tr v-for="deployment in householdHistory.deployments" :key="deployment.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                  <td class="px-4 py-3 text-sm">
                    <span
                      class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      :class="{
                        'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300': deployment.type === 'deployment',
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': deployment.type === 'swap',
                        'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': deployment.type !== 'deployment' && deployment.type !== 'swap'
                      }"
                    >
                      {{ humanize(deployment.type) }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                    <div v-if="deployment.type === 'swap'" class="space-y-1">
                      <div class="text-red-600 dark:text-red-400">
                        <span class="text-xs">Out:</span> {{ deployment.removedContainerSerial || deployment.removedContainerId }}
                      </div>
                      <div class="text-green-600 dark:text-green-400">
                        <span class="text-xs">In:</span> {{ deployment.installedContainerSerial || deployment.installedContainerId }}
                      </div>
                    </div>
                    <div v-else>
                      {{ deployment.containerSerial || deployment.installedContainerSerial || deployment.installedContainerId || deployment.containerId }}
                    </div>
                  </td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(deployment.performedAt) }}</td>
                  <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ deployment.performedBy || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="!householdHistory.assignments?.length && !householdHistory.deployments?.length && !householdHistory.collections?.length" class="text-center py-12">
          <p class="text-neutral-500 dark:text-neutral-400">No history available for this household.</p>
        </div>
      </div>

      <template #footer>
        <button
          @click="closeHistoryModal"
          class="px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Close
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import useHomebaseHouseholds from '@/composables/useHomebaseHouseholds'
import Modal from '@/components/ui/Modal.vue'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'

export default {
  name: 'HomeBaseHouseHolds',
  components: { Modal, ErrorState, AlertBanner },
  setup() {
    const {
      households,
      householdHistory,
      loading,
      historyLoading,
      error,
      params,
      fetchHouseholds,
      fetchHouseholdHistory
    } = useHomebaseHouseholds()

    const filters = reactive({
      community: '',
      status: '',
      hasContainer: ''
    })

    const showHistoryModal = ref(false)
    const selectedHousehold = ref(null)
    const historyError = ref(null)

    const applyFilters = () => {
      params.value.community = filters.community.trim()
      params.value.status = filters.status
      params.value.hasContainer = filters.hasContainer
      fetchHouseholds()
    }

    const reload = () => {
      applyFilters()
    }

    const sortState = reactive({ key: 'createdAt', dir: 'desc' })
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    const sortedAndFiltered = computed(() => {
      return [...households.value].sort((a, b) => {
        const va = (a[sortState.key] || '')
        const vb = (b[sortState.key] || '')
        if (va < vb) return sortState.dir === 'asc' ? -1 : 1
        if (va > vb) return sortState.dir === 'asc' ? 1 : -1
        return 0
      })
    })

    const limitReached = computed(() => households.value.length >= (params.value.limit || 100))

    const formatDateTime = (value) => (value ? value.slice(0, 16).replace('T', ' ') : '-')

    // 'deployment_task' -> 'Deployment Task', 'swap' -> 'Swap'
    const humanize = (value) => {
      if (!value) return value
      return String(value)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    }

    const loadHistory = async () => {
      if (!selectedHousehold.value) return
      historyError.value = null
      // The composable writes history failures into the page-level error ref;
      // snapshot it so a modal failure doesn't trigger the page error banner.
      const pageError = error.value
      try {
        await fetchHouseholdHistory(selectedHousehold.value.id)
      } catch (err) {
        historyError.value = err.detail || err.message || 'Failed to load household history'
        error.value = pageError
      }
    }

    const viewHistory = (household) => {
      selectedHousehold.value = household
      showHistoryModal.value = true
      loadHistory()
    }

    const closeHistoryModal = () => {
      showHistoryModal.value = false
      selectedHousehold.value = null
      householdHistory.value = null
      historyError.value = null
    }

    onMounted(() => {
      fetchHouseholds()
    })

    return {
      households,
      householdHistory,
      loading,
      historyLoading,
      error,
      filters,
      showHistoryModal,
      selectedHousehold,
      historyError,
      reload,
      sortedAndFiltered,
      sortBy,
      sortState,
      ariaSort,
      limitReached,
      formatDateTime,
      humanize,
      viewHistory,
      loadHistory,
      closeHistoryModal
    }
  }
}
</script>
